import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { googleLogout } from '@react-oauth/google';

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

interface StoredSession {
  user: User;
  expiresAt: number; // Unix timestamp in ms
}

interface AuthContextType {
  user: User | null;
  login: (credential: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_KEY = 'userSession';
const SESSION_DURATION_MS = 365 * 24 * 60 * 60 * 1000; // 1 year

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Try to restore from persistent session first
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (sessionData) {
      try {
        const session: StoredSession = JSON.parse(sessionData);
        if (session.expiresAt > Date.now()) {
          setUser(session.user);
          setIsLoading(false);
          return;
        }
        // Session expired, clean up
        localStorage.removeItem(SESSION_KEY);
      } catch {
        localStorage.removeItem(SESSION_KEY);
      }
    }

    // Migrate legacy authToken to new session format
    const legacyToken = localStorage.getItem('authToken');
    if (legacyToken) {
      try {
        const tokenParts = legacyToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(
            atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/'))
          );
          const user: User = {
            id: payload.sub,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
          };
          const session: StoredSession = {
            user,
            expiresAt: Date.now() + SESSION_DURATION_MS,
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(session));
          localStorage.removeItem('authToken');
          setUser(user);
        }
      } catch (error) {
        console.error('Error migrating legacy session:', error);
        localStorage.removeItem('authToken');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (credential: string) => {
    try {
      // Decode the Google JWT token directly on frontend
      const tokenParts = credential.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      // Decode the payload (base64 URL decode)
      const payload = JSON.parse(
        atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/'))
      );

      // Extract user information from Google token
      const user: User = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        picture: payload.picture,
      };

      // Store session with 1-year expiration
      const session: StoredSession = {
        user,
        expiresAt: Date.now() + SESSION_DURATION_MS,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));

      setUser(user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('authToken');
    googleLogout();
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};